import { useEffect, useRef, useState } from "react";
import { gaTrack, metaTrack, getConsent } from "@/lib/analytics";
import { trackEvent } from "@/lib/tracker";

/* Calendly inline booker.
 *
 * Exists because /walkthrough promised "book a 15-minute walkthrough" and then
 * rendered a contact form whose required field asked for a paragraph about your
 * team. Twelve visits to the contact/walkthrough forms in 30 days produced zero
 * submissions, ever. The button said booking; the page said homework.
 *
 * Two things this has to get right beyond rendering the widget:
 *
 * 1. CONVERSIONS. The booking happens inside Calendly's iframe, so none of our
 *    own analytics see it. Left alone, walkthrough traffic would convert at a
 *    reported 0% in GA and Meta while actually working, which is exactly the
 *    kind of instrumentation hole that makes a working funnel look dead. We
 *    listen for Calendly's postMessage events and fire the conversions here.
 *
 * 2. CONSENT. Calendly is a third party that sets its own cookies. The cookie
 *    banner is the user's answer on that, so if they declined we don't load the
 *    script at all and fall back to the form. Loading it anyway would contradict
 *    what the privacy policy tells people (and what App Review was told).
 */

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CSS_HREF = "https://assets.calendly.com/assets/external/widget.css";

/** The booking URL. Env var wins so it can change without a code edit, with a
 *  hardcoded default because `.env.*` is gitignored: an env-only setup works on
 *  this laptop and silently renders nothing anywhere else. It's a public
 *  booking link, not a secret, so committing it costs nothing. */
export const CALENDLY_URL = (
  import.meta.env.VITE_CALENDLY_URL || "https://calendly.com/jsutton-siftcareers"
).trim();

/** Calendly's widget messages all arrive as {event: "calendly.*"} from their origin. */
function isCalendlyEvent(e) {
  return (
    e.origin === "https://calendly.com" &&
    e.data &&
    typeof e.data.event === "string" &&
    e.data.event.startsWith("calendly.")
  );
}

export default function CalendlyEmbed({ url = CALENDLY_URL, onUnavailable }) {
  const holder = useRef(null);
  const [failed, setFailed] = useState(false);

  const declined = (() => {
    try {
      return getConsent() === "declined";
    } catch {
      return false;
    }
  })();

  const blocked = !url || declined;

  // Tell the parent to render its fallback instead of an empty box.
  useEffect(() => {
    if (blocked || failed) onUnavailable?.(declined ? "consent" : failed ? "script" : "unset");
  }, [blocked, failed, declined, onUnavailable]);

  // Load the widget assets once, then let Calendly hydrate the holder div.
  useEffect(() => {
    if (blocked) return undefined;

    if (!document.querySelector(`link[href="${CSS_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CSS_HREF;
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) return undefined;

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    // An ad blocker or a Calendly outage should degrade to the form, not to a
    // blank page where the CTA used to be.
    script.onerror = () => setFailed(true);
    document.body.appendChild(script);
    return undefined;
  }, [blocked]);

  // Conversion tracking. `event_scheduled` is the one that means a real booking;
  // the earlier events are useful for seeing where people drop inside the widget.
  useEffect(() => {
    if (blocked) return undefined;

    const onMessage = (e) => {
      if (!isCalendlyEvent(e)) return;
      switch (e.data.event) {
        case "calendly.event_type_viewed":
          trackEvent("walkthrough_booker_viewed", { account_type: "organization" });
          break;
        case "calendly.date_and_time_selected":
          trackEvent("walkthrough_time_selected", { account_type: "organization" });
          break;
        case "calendly.event_scheduled":
          // The conversion. Mirrors what the contact form fired so the two are
          // comparable in GA, and so Meta still sees walkthrough leads.
          gaTrack("generate_lead", { location: "walkthrough_calendly", account_type: "organization" });
          metaTrack("Lead");
          trackEvent("walkthrough_booked", { account_type: "organization", source: "calendly" });
          break;
        default:
          break;
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [blocked]);

  if (blocked || failed) return null;

  return (
    <div
      ref={holder}
      className="calendly-inline-widget rounded-2xl overflow-hidden border border-line bg-surface"
      data-url={`${url}${url.includes("?") ? "&" : "?"}hide_gdpr_banner=1`}
      style={{ minWidth: 320, height: 700 }}
    />
  );
}
